import { useEffect, useState } from 'react';
import api from '../../utils/api';

/**
 * Document Numbering — per-series auto-number rules.
 *
 * Each of the four series (invoice / delivery_note / purchase_order /
 * customer_payment) has:
 *   • prefix  — string, supports a literal `{FY}` token
 *   • suffix  — string, supports `{FY}` token
 *   • padding — int (zero-pad width for the running number)
 *   • next_number — int (the next value to issue)
 *   • enabled — toggle (when off the form keeps its old behaviour of letting
 *     the user type the number manually)
 *
 * On save we PUT /companies/{id} with just the `numbering_config` slice so
 * none of the other company fields are touched.
 *
 * Lives as a section on the CompanySettings page (toggled via the page-level
 * Settings / Document Numbering tab bar).
 */
const SERIES = [
  { key: 'invoice',          label: 'Sales Invoice',     example: 'ZT/BH/00001/26' },
  { key: 'delivery_note',    label: 'Delivery Note',     example: 'ZT/DN/00001/26' },
  { key: 'purchase_order',   label: 'Purchase Order',    example: 'ZT/PO/00001/26' },
  { key: 'customer_payment', label: 'Customer Payment',  example: 'RV/ZT/00001/26' },
];

const blankSeries = () => ({ prefix: '', suffix: '/{YY}', padding: 5, next_number: 1, enabled: false });

const DocumentNumberingPanel = ({ company, onSaved }) => {
  const [config, setConfig] = useState({});
  const [previews, setPreviews] = useState({});
  const [saving, setSaving] = useState(false);

  // Hydrate from the selected company. Merge over defaults so blanks
  // become editable inputs (not `undefined` -> React warning).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await Promise.resolve();
      if (cancelled) return;
      const stored = (company && company.numbering_config) || {};
      const merged = {};
      SERIES.forEach((s) => {
        merged[s.key] = { ...blankSeries(), ...(stored[s.key] || {}) };
      });
      setConfig(merged);
    })();
    return () => { cancelled = true; };
  }, [company?.id, company]);

  // Live previews via the peek endpoint so the user sees exactly what will
  // be generated (including the resolved {FY} token).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(
        SERIES.map(async (s) => {
          try {
            const res = await api.get(`/companies/numbering/peek?series=${s.key}`);
            next[s.key] = res.data?.formatted || '';
          } catch {
            next[s.key] = '';
          }
        }),
      );
      if (!cancelled) setPreviews(next);
    })();
    return () => { cancelled = true; };
  }, [company?.id]);

  const refreshPreviews = async () => {
    const next = {};
    await Promise.all(
      SERIES.map(async (s) => {
        try {
          const res = await api.get(`/companies/numbering/peek?series=${s.key}`);
          next[s.key] = res.data?.formatted || '';
        } catch {
          next[s.key] = '';
        }
      }),
    );
    setPreviews(next);
  };

  // Update one cell of one series.
  const onField = (seriesKey, field, value) => {
    setConfig((c) => ({
      ...c,
      [seriesKey]: { ...c[seriesKey], [field]: value },
    }));
  };

  // Client-side preview that doesn't hit the network (instant feedback on
  // each keystroke). Mirrors the backend formatter: supports both `{YY}`
  // (2-digit FY year e.g. '26') and `{FY}` (full label e.g. 'FY 2026').
  const localPreview = (s) => {
    const cfg = config[s.key] || blankSeries();
    const seenFy = (previews[s.key] || '').match(/FY \d{4}/)?.[0] || 'FY 2026';
    const yearMatch = seenFy.match(/(\d{4})/);
    const yy = yearMatch ? yearMatch[1].slice(-2) : '26';
    const sub = (v) => (v || '').replace('{YY}', yy).replace('{FY}', seenFy);
    const pref = sub(cfg.prefix);
    const suff = sub(cfg.suffix);
    const pad = Math.max(parseInt(cfg.padding, 10) || 0, 1);
    const num = String(parseInt(cfg.next_number, 10) || 1).padStart(pad, '0');
    return `${pref}${num}${suff}`;
  };

  const save = async () => {
    if (!company?.id) return;
    setSaving(true);
    try {
      // Normalise numeric inputs before send.
      const cleaned = {};
      SERIES.forEach((s) => {
        const cfg = config[s.key] || blankSeries();
        cleaned[s.key] = {
          prefix: cfg.prefix || '',
          suffix: cfg.suffix || '',
          padding: Math.max(parseInt(cfg.padding, 10) || 5, 1),
          next_number: Math.max(parseInt(cfg.next_number, 10) || 1, 1),
          enabled: !!cfg.enabled,
        };
      });
      await api.put(`/companies/${company.id}`, { numbering_config: cleaned });
      await refreshPreviews();
      if (onSaved) onSaved(cleaned);
      alert('Document numbering settings saved.');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to save numbering settings');
    } finally {
      setSaving(false);
    }
  };

  if (!company) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-sm text-gray-500">
        Pick a company above to configure its document numbering.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow" data-testid="doc-numbering-panel">
      <div className="bg-gradient-to-r from-blue-700 to-green-700 px-6 py-4 rounded-t-lg">
        <h2 className="text-xl font-bold text-white">📐 Document Numbering — {company.name}</h2>
        <p className="text-xs text-white/80 mt-1">
          Tip: use the literal token <code className="bg-white/20 px-1 rounded">{'{FY}'}</code> in the prefix or suffix to inject the active fiscal-year name (e.g. <code className="bg-white/20 px-1 rounded">FY 2026</code>).
        </p>
      </div>

      <div className="p-6 overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="numbering-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b">
              <th className="px-2 py-2">Enabled</th>
              <th className="px-2 py-2">Series</th>
              <th className="px-2 py-2">Prefix</th>
              <th className="px-2 py-2">Suffix</th>
              <th className="px-2 py-2 text-right">Padding</th>
              <th className="px-2 py-2 text-right">Next #</th>
              <th className="px-2 py-2">Preview</th>
            </tr>
          </thead>
          <tbody>
            {SERIES.map((s) => {
              const cfg = config[s.key] || blankSeries();
              return (
                <tr key={s.key} className="border-b hover:bg-slate-50" data-testid={`numbering-row-${s.key}`}>
                  <td className="px-2 py-3">
                    <input
                      type="checkbox"
                      checked={!!cfg.enabled}
                      onChange={(e) => onField(s.key, 'enabled', e.target.checked)}
                      data-testid={`numbering-enabled-${s.key}`}
                    />
                  </td>
                  <td className="px-2 py-3 font-medium">{s.label}</td>
                  <td className="px-2 py-3">
                    <input
                      type="text"
                      value={cfg.prefix}
                      onChange={(e) => onField(s.key, 'prefix', e.target.value)}
                      placeholder="ZT/BH/"
                      className="border border-slate-300 rounded px-2 py-1 w-32 font-mono text-xs"
                      data-testid={`numbering-prefix-${s.key}`}
                    />
                  </td>
                  <td className="px-2 py-3">
                    <input
                      type="text"
                      value={cfg.suffix}
                      onChange={(e) => onField(s.key, 'suffix', e.target.value)}
                      placeholder="/{YY}"
                      className="border border-slate-300 rounded px-2 py-1 w-32 font-mono text-xs"
                      data-testid={`numbering-suffix-${s.key}`}
                    />
                  </td>
                  <td className="px-2 py-3 text-right">
                    <input
                      type="number"
                      min="1"
                      max="12"
                      value={cfg.padding}
                      onChange={(e) => onField(s.key, 'padding', e.target.value)}
                      className="border border-slate-300 rounded px-2 py-1 w-16 text-right font-mono"
                      data-testid={`numbering-padding-${s.key}`}
                    />
                  </td>
                  <td className="px-2 py-3 text-right">
                    <input
                      type="number"
                      min="1"
                      value={cfg.next_number}
                      onChange={(e) => onField(s.key, 'next_number', e.target.value)}
                      className="border border-slate-300 rounded px-2 py-1 w-24 text-right font-mono"
                      data-testid={`numbering-next-${s.key}`}
                    />
                  </td>
                  <td className="px-2 py-3 font-mono text-emerald-700">
                    {cfg.enabled ? localPreview(s) : <span className="text-slate-400 italic">disabled</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="mt-4 text-xs text-slate-500 space-y-1">
          <p>• The series counter advances automatically the first time a document with the matching prefix/suffix is saved.</p>
          <p>• If you edit the prefix or suffix mid-year, existing documents are not retro-numbered — only future generations use the new pattern.</p>
          <p>• Resetting <code>Next #</code> manually (e.g. for a new fiscal year) is supported. Make sure the new number doesn&apos;t collide with an existing document number.</p>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="bg-gradient-to-r from-blue-700 to-green-700 text-white px-6 py-2 rounded-lg hover:opacity-90 disabled:opacity-50"
            data-testid="numbering-save-btn"
          >
            {saving ? 'Saving…' : '💾 Save Numbering Rules'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentNumberingPanel;
